import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import useMediaQuery from "@mui/material/useMediaQuery";
import toast from "react-hot-toast";
import Scrollto from "../../../tools/scrollTo";
import PanelFrame from "../../../ui/PanelFrame";
import SiteButton from "../../../ui/SiteButton";
import { useLang } from "../../../../i18n/LangContext";
import {
  checkMutop100,
  claimMutop100,
  clearVoteAction,
  fetchVotePage,
  startTop100Arena,
  statusTop100Arena,
} from "../../../../store/reducers/reducerVote";
import "./Vote.scss";

const STATUS_KEYS = {
  READY_TO_VOTE: "vote_status_ready",
  PENDING: "vote_status_pending",
  CHECK_VOTE: "vote_status_check",
  CLAIM_REWARD: "vote_status_claim",
  VERIFIED: "vote_status_verified",
  BCOIN_RECEIVED: "vote_status_bcoin_received",
  ALREADY_CLAIMED: "vote_status_already_claimed",
  NO_VERIFIED_VOTE_FOUND: "vote_status_no_vote",
  TRY_AGAIN_LATER: "vote_status_try_again",
  PROVIDER_TEMPORARILY_UNAVAILABLE: "vote_status_unavailable",
  RATE_LIMIT: "vote_status_try_again",
  INTERNAL_ERROR: "vote_status_unavailable",
};

function statusLabel(t, code) {
  if (!code) return t("vote_status_ready");
  const key = STATUS_KEYS[code] || STATUS_KEYS[String(code).toUpperCase()];
  return key ? t(key) : String(code);
}

function providerDisplayName(key) {
  if (key === "top100arena") return "Top100Arena";
  if (key === "mutop100") return "MUtop100";
  return key;
}

function mapDbStatus(status) {
  if (!status) return "READY_TO_VOTE";
  const s = String(status).toLowerCase();
  if (s === "rewarded") return "BCOIN_RECEIVED";
  if (s === "pending") return "PENDING";
  if (s === "claiming") return "PENDING";
  if (s === "already_claimed") return "ALREADY_CLAIMED";
  if (s === "failed") return "TRY_AGAIN_LATER";
  return String(status).toUpperCase();
}

const Vote = () => {
  const matches = useMediaQuery("(max-width:500px)");
  const { t, isHe } = useLang();
  const dispatch = useDispatch();

  const user = useSelector((state) => state.reducerUser.userLogin.data);
  const page = useSelector((state) => state.reducerVote.page);
  const action = useSelector((state) => state.reducerVote.action);

  const [busy, setBusy] = useState("");
  const [localStatus, setLocalStatus] = useState({
    top100arena: "READY_TO_VOTE",
    mutop100: "READY_TO_VOTE",
  });

  const account = useMemo(() => {
    if (page?.data?.account) return page.data.account;
    return user?.username || "";
  }, [page?.data?.account, user?.username]);

  const providers = page?.data?.providers || [];
  const recent = page?.data?.recent || [];

  const refreshPage = useCallback(() => {
    dispatch(fetchVotePage());
  }, [dispatch]);

  useEffect(() => {
    refreshPage();
    return () => {
      dispatch(clearVoteAction());
    };
  }, [dispatch, refreshPage]);

  useEffect(() => {
    const latest = page?.data?.latest;
    if (!latest) return;
    setLocalStatus((prev) => ({
      ...prev,
      top100arena: mapDbStatus(latest.top100arena?.Status) || prev.top100arena,
      mutop100: mapDbStatus(latest.mutop100?.Status) || prev.mutop100,
    }));
  }, [page?.data?.latest]);

  useEffect(() => {
    if (!action?.code) return;
    const code = action.code;
    const provider = action.data?.provider;
    if (provider === "top100arena" || code === "PENDING" || code === "READY_TO_VOTE") {
      if (
        [
          "READY_TO_VOTE",
          "PENDING",
          "BCOIN_RECEIVED",
          "ALREADY_CLAIMED",
          "VERIFIED",
        ].includes(code)
      ) {
        setLocalStatus((prev) => ({ ...prev, top100arena: code }));
      }
    }
    if (
      [
        "CLAIM_REWARD",
        "NO_VERIFIED_VOTE_FOUND",
        "BCOIN_RECEIVED",
        "ALREADY_CLAIMED",
        "TRY_AGAIN_LATER",
        "PROVIDER_TEMPORARILY_UNAVAILABLE",
      ].includes(code)
    ) {
      // Prefer mutop100 for check/claim codes; BCOIN_RECEIVED may also come from T100 status.
      if (action.data?.canClaim != null || action.data?.bcoinAdded != null) {
        setLocalStatus((prev) => ({ ...prev, mutop100: code }));
      } else if (code === "BCOIN_RECEIVED" && action.data?.rewardedAt) {
        setLocalStatus((prev) => ({ ...prev, top100arena: code }));
      }
    }
  }, [action]);

  const showCodeToast = (code, ok) => {
    if (!code) return;
    const msg = statusLabel(t, code);
    if (code === "BCOIN_RECEIVED") toast.success(msg);
    else if (!ok || code === "PROVIDER_TEMPORARILY_UNAVAILABLE") toast.error(msg);
    else if (
      code === "NO_VERIFIED_VOTE_FOUND" ||
      code === "ALREADY_CLAIMED" ||
      code === "TRY_AGAIN_LATER" ||
      code === "RATE_LIMIT"
    ) {
      toast(msg);
    }
  };

  const handleTop100Vote = async () => {
    setBusy("t100_vote");
    try {
      const result = await dispatch(startTop100Arena());
      const data = result.payload;
      if (result.error || !data?.ok) {
        showCodeToast(data?.code || "INTERNAL_ERROR", false);
        return;
      }
      setLocalStatus((prev) => ({ ...prev, top100arena: "PENDING" }));
      if (data.voteUrl) {
        window.open(data.voteUrl, "_blank", "noopener,noreferrer");
      }
      refreshPage();
    } finally {
      setBusy("");
    }
  };

  const handleTop100Check = async () => {
    setBusy("t100_check");
    const wasPending = localStatus.top100arena === "PENDING";
    try {
      const result = await dispatch(statusTop100Arena());
      const data = result.payload;
      if (result.error || !data?.ok) {
        showCodeToast(data?.code || "INTERNAL_ERROR", false);
        return;
      }
      // Status does not grant BCoin. BCOIN_RECEIVED is also returned for an
      // older rewarded row, so the success toast is only for a pending vote
      // that this check finds newly rewarded.
      if (data.code === "BCOIN_RECEIVED") {
        setLocalStatus((prev) => ({ ...prev, top100arena: "BCOIN_RECEIVED" }));
        refreshPage();
        if (wasPending) showCodeToast(data.code, true);
        return;
      }
      showCodeToast(data.code, true);
    } finally {
      setBusy("");
    }
  };

  const handleMutopVote = (voteUrl) => {
    if (voteUrl) window.open(voteUrl, "_blank", "noopener,noreferrer");
    setLocalStatus((prev) => ({ ...prev, mutop100: "CHECK_VOTE" }));
  };

  const handleMutopCheck = async () => {
    setBusy("mu_check");
    try {
      const result = await dispatch(checkMutop100());
      const data = result.payload;
      if (result.error || data?.ok === false) {
        showCodeToast(data?.code || "PROVIDER_TEMPORARILY_UNAVAILABLE", false);
        setLocalStatus((prev) => ({
          ...prev,
          mutop100: data?.code || "PROVIDER_TEMPORARILY_UNAVAILABLE",
        }));
        return;
      }
      setLocalStatus((prev) => ({ ...prev, mutop100: data.code }));
      showCodeToast(data.code, true);
    } finally {
      setBusy("");
    }
  };

  const handleMutopClaim = async () => {
    setBusy("mu_claim");
    try {
      const result = await dispatch(claimMutop100());
      const data = result.payload;
      if (result.error || data?.ok === false) {
        showCodeToast(data?.code || "PROVIDER_TEMPORARILY_UNAVAILABLE", false);
        setLocalStatus((prev) => ({
          ...prev,
          mutop100: data?.code || "PROVIDER_TEMPORARILY_UNAVAILABLE",
        }));
        return;
      }
      setLocalStatus((prev) => ({ ...prev, mutop100: data.code }));
      showCodeToast(data.code, true);
      if (data.code === "BCOIN_RECEIVED") refreshPage();
    } finally {
      setBusy("");
    }
  };

  const renderProviderCard = (provider) => {
    const key = provider.key;
    const name = provider.displayName || providerDisplayName(key);
    const statusCode = localStatus[key] || "READY_TO_VOTE";
    const rewardText = t("vote_plus_one_bcoin");

    if (key === "top100arena") {
      return (
        <article key={key} className="mu-vote__card">
          <header className="mu-vote__card-head">
            <h3 className="mu-vote__provider">{name}</h3>
            <span className="mu-vote__reward">{rewardText}</span>
          </header>
          <p className="mu-vote__hint">{t("vote_t100_hint")}</p>
          <div className="mu-vote__status-row">
            <span className="mu-vote__status-label">{t("vote_status")}</span>
            <span className="mu-vote__status-value">
              {statusLabel(t, statusCode)}
            </span>
          </div>
          <div className="mu-vote__card-actions">
            <SiteButton
              variant="primary"
              disabled={!!busy}
              onClick={handleTop100Vote}
            >
              {busy === "t100_vote" ? "…" : t("vote_now")}
            </SiteButton>
            <SiteButton
              variant="secondary"
              disabled={!!busy}
              onClick={handleTop100Check}
            >
              {busy === "t100_check" ? "…" : t("vote_check")}
            </SiteButton>
          </div>
        </article>
      );
    }

    if (key === "mutop100") {
      const canClaim = statusCode === "CLAIM_REWARD";
      return (
        <article key={key} className="mu-vote__card">
          <header className="mu-vote__card-head">
            <h3 className="mu-vote__provider">{name}</h3>
            <span className="mu-vote__reward">{rewardText}</span>
          </header>
          <p className="mu-vote__hint">{t("vote_mutop_hint")}</p>
          <div className="mu-vote__account-box">
            <span className="mu-vote__account-label">
              {t("vote_account_to_use")}
            </span>
            <strong className="mu-vote__account-value">{account || "—"}</strong>
          </div>
          <div className="mu-vote__status-row">
            <span className="mu-vote__status-label">{t("vote_status")}</span>
            <span className="mu-vote__status-value">
              {statusLabel(t, statusCode)}
            </span>
          </div>
          <div className="mu-vote__card-actions">
            <SiteButton
              variant="primary"
              disabled={!!busy}
              onClick={() => handleMutopVote(provider.voteUrl)}
            >
              {t("vote_now")}
            </SiteButton>
            <SiteButton
              variant="secondary"
              disabled={!!busy}
              onClick={handleMutopCheck}
            >
              {busy === "mu_check" ? "…" : t("vote_check")}
            </SiteButton>
            <SiteButton
              variant="primary"
              disabled={!!busy || !canClaim}
              onClick={handleMutopClaim}
            >
              {busy === "mu_claim" ? "…" : t("vote_claim")}
            </SiteButton>
          </div>
        </article>
      );
    }

    // Future providers: generic vote link if voteUrl present
    return (
      <article key={key} className="mu-vote__card">
        <header className="mu-vote__card-head">
          <h3 className="mu-vote__provider">{name}</h3>
          <span className="mu-vote__reward">{rewardText}</span>
        </header>
        {provider.voteUrl ? (
          <div className="mu-vote__card-actions">
            <SiteButton
              variant="primary"
              href={provider.voteUrl}
            >
              {t("vote_now")}
            </SiteButton>
          </div>
        ) : null}
      </article>
    );
  };

  const statusText = (row) => {
    const s = String(row.Status || "").toLowerCase();
    if (s === "rewarded") return t("vote_claimed");
    if (s === "pending" || s === "claiming") return t("vote_status_pending");
    if (s === "already_claimed") return t("vote_status_already_claimed");
    return statusLabel(t, mapDbStatus(row.Status));
  };

  return (
    <div className="mu-vote" dir={isHe ? "rtl" : "ltr"}>
      <Scrollto position={matches ? 311 : 200} />
      <PanelFrame title={t("vote_page_title")}>
        <div className="mu-vote__intro">
          <p className="mu-vote__lead">{t("vote_page_lead")}</p>
          <p className="mu-vote__sub">{t("vote_page_sub")}</p>
          <div className="mu-vote__meta">
            <div>
              <span className="mu-vote__meta-label">{t("vote_account")}</span>
              <strong>{account || "—"}</strong>
            </div>
            <div>
              <span className="mu-vote__meta-label">{t("vote_reward")}</span>
              <strong className="mu-vote__meta-reward">
                {t("vote_reward_value")}
              </strong>
            </div>
          </div>
        </div>

        <div className="mu-vote__providers">
          {page.status === "pending" && !providers.length ? (
            <p className="mu-vote__loading">…</p>
          ) : (
            (providers.length
              ? providers
              : [
                  { key: "top100arena", displayName: "Top100Arena" },
                  {
                    key: "mutop100",
                    displayName: "MUtop100",
                    voteUrl: "https://www.mutop100.com/muonline/mu-beginning-351",
                  },
                ]
            ).map(renderProviderCard)
          )}
        </div>

        {recent.length > 0 ? (
          <section className="mu-vote__recent">
            <h3 className="mu-vote__recent-title">{t("vote_recent")}</h3>
            <ul className="mu-vote__recent-list">
              {recent.map((row) => (
                <li key={row.RewardID}>
                  <span className="mu-vote__recent-provider">
                    {providerDisplayName(row.Provider)}
                  </span>
                  <span className="mu-vote__recent-amount">
                    {t("vote_plus_one_bcoin")}
                  </span>
                  <span className="mu-vote__recent-status">{statusText(row)}</span>
                </li>
              ))}
            </ul>
          </section>
        ) : null}
      </PanelFrame>
    </div>
  );
};

export default Vote;
